import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LifeBuoy, X, Search, Paperclip, Flag, MessageSquare, ArrowRight } from 'lucide-react';
import { useLocalization } from '../contexts/LocalizationContext';

const STORAGE_KEY = 'moko_tickets_announcement_dismissed_v1';

export default function TicketsAnnouncementModal() {
  const { t } = useLocalization();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const dismissed = localStorage.getItem(STORAGE_KEY);
    if (dismissed) return;
    // Slight delay so the dashboard renders first.
    const id = setTimeout(() => setOpen(true), 1200);
    return () => clearTimeout(id);
  }, []);

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, new Date().toISOString());
    setOpen(false);
  };

  const explore = () => {
    dismiss();
    navigate('/tickets');
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div
        role="dialog"
        aria-modal="true"
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto animate-[modalIn_220ms_ease-out]"
      >
        {/* Hero */}
        <div className="relative overflow-hidden rounded-t-2xl bg-gradient-to-br from-emerald-600 via-emerald-700 to-teal-700 px-6 py-5 text-white">
          <div className="absolute inset-0 opacity-[0.15]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.5) 1px, transparent 0)', backgroundSize: '24px 24px' }} />
          <div className="absolute -top-20 -right-10 w-56 h-56 rounded-full bg-white/10 blur-2xl" />
          <button
            onClick={dismiss}
            className="absolute top-3 right-3 p-1.5 rounded-lg hover:bg-white/15 transition-colors"
            aria-label="Close"
          >
            <X size={16} className="text-white/80" />
          </button>
          <div className="relative flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-white/15 border border-white/20">
              <LifeBuoy size={22} className="text-white" />
            </div>
            <div>
              <div className="text-[10px] font-semibold tracking-widest uppercase text-emerald-200">
                {t('ticketsAnnounce.eyebrow')}
              </div>
              <h2 className="text-xl font-bold mt-0.5">{t('ticketsAnnounce.title')}</h2>
              <p className="text-sm text-emerald-100 mt-0.5">{t('ticketsAnnounce.subtitle')}</p>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-5">
          <p className="text-sm text-gray-700 leading-relaxed">{t('ticketsAnnounce.lead')}</p>

          <ul className="space-y-2.5">
            <Benefit icon={<Search size={14} />} text={t('ticketsAnnounce.benefit1')} />
            <Benefit icon={<Paperclip size={14} />} text={t('ticketsAnnounce.benefit2')} />
            <Benefit icon={<Flag size={14} />} text={t('ticketsAnnounce.benefit3')} />
          </ul>

          {/* Channel matrix */}
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 space-y-2.5">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
              {t('ticketsAnnounce.matrixTitle')}
            </div>
            <ChannelRow primary={t('ticketsAnnounce.channelTickets')} description={t('ticketsAnnounce.channelTicketsDesc')} accent />
            <ChannelRow primary={t('ticketsAnnounce.channelChat')} description={t('ticketsAnnounce.channelChatDesc')} />
            <ChannelRow primary={t('ticketsAnnounce.channelTeams')} description={t('ticketsAnnounce.channelTeamsDesc')} />
          </div>

          <p className="text-[11px] text-gray-500 inline-flex items-center gap-1.5">
            <MessageSquare size={12} className="text-gray-400" />
            {t('ticketsAnnounce.footer')}
          </p>
        </div>

        {/* Actions */}
        <div className="px-6 py-3 border-t border-gray-100 flex justify-end gap-2 bg-gray-50/40 rounded-b-2xl">
          <button
            onClick={dismiss}
            className="px-4 py-2 rounded-lg text-gray-600 text-sm hover:bg-gray-100 transition-colors"
          >
            {t('ticketsAnnounce.later')}
          </button>
          <button
            onClick={explore}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-gradient-to-r from-emerald-600 to-emerald-700 text-white text-sm font-semibold shadow-sm hover:from-emerald-700 hover:to-emerald-800 transition-all"
          >
            {t('ticketsAnnounce.cta')} <ArrowRight size={14} />
          </button>
        </div>
      </div>

      <style>{`
        @keyframes modalIn {
          0% { opacity: 0; transform: translateY(8px) scale(0.98); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </div>
  );
}

function Benefit({ icon, text }: { icon: React.ReactNode; text: string }) {
  return (
    <li className="flex items-start gap-2.5">
      <span className="mt-0.5 w-6 h-6 rounded-md bg-emerald-50 text-emerald-700 flex items-center justify-center shrink-0">
        {icon}
      </span>
      <span className="text-sm text-gray-700 leading-relaxed">{text}</span>
    </li>
  );
}

function ChannelRow({ primary, description, accent }: { primary: string; description: string; accent?: boolean }) {
  return (
    <div className="flex items-baseline gap-3">
      <span className={`shrink-0 text-xs font-semibold ${accent ? 'text-emerald-700' : 'text-gray-700'} min-w-[180px]`}>
        {primary}
      </span>
      <span className="text-xs text-gray-600">{description}</span>
    </div>
  );
}
